/**
 * Merge Contact Section — "Merge into…" quick action on the contact detail
 * sidebar. Search for a target contact, preview the field-level merge
 * result, then confirm. Gated behind `crm:delete` (destructive, absorbs and
 * archives the viewed contact) — same permission tier as Delete Contact.
 *
 * Direction semantics (load-bearing, inverted from the page's viewpoint):
 * the VIEWED contact (`contact` prop) is the SOURCE — it gets absorbed and
 * archived. The picked candidate is the TARGET — it survives. The merge
 * service's primary/secondary naming is from the survivor's perspective, so
 * `primaryId` = target, `secondaryId` = the viewed contact. Preview and
 * merge calls are `(target.id, contact.id)`; navigation lands on the
 * target's detail page.
 *
 * Structural precedent: ContactSidebar.tsx's Moderation confirm-Modal
 * (delete flow) for the Modal shape/copy tone, and its `QuickActionButton`
 * for the trigger row. Search input follows AddressesSection.tsx's
 * self-contained-section conventions (own `useCRM` calls, own toast
 * notify()).
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Merge } from 'lucide-react'
import {
  Button, EmptyState, Input, Modal, ModalBody, ModalContent, ModalFooter,
  ModalHeader, ModalTitle, Spinner, Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow, useToast,
} from '@/components/ui'
import { useCRM } from '@/hooks/useCRM'
import { usePermission } from '@/hooks/usePermission'
import type { CrmContactDetail, CrmContactSummary, MergePreview } from '@/types/crm'

const FIELD_LABELS: Record<string, string> = {
  display_name: 'Name', email: 'Email', birthday: 'Birthday',
  language_preference: 'Language', notes: 'Notes', loyalty_tier: 'Loyalty tier',
  loyalty_points: 'Loyalty points', loyalty_enrolled_at: 'Loyalty enrolled',
  company_id: 'Company', job_title: 'Job title', tags: 'Tags',
}

const FATE_LABELS: Record<string, string> = {
  soft_deleted: 'archived',
  anonymized: 'anonymized',
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
  return String(value)
}

function insightCounts(result: unknown): string {
  if (!result || typeof result !== 'object') return '—'
  const entries = Object.entries(result as Record<string, unknown>)
    .map(([key, v]) => {
      const n = Array.isArray(v) ? v.length : (v && typeof v === 'object' ? Object.keys(v).length : 1)
      return n > 0 ? `${key} ${n}` : null
    })
    .filter(Boolean)
  return entries.length ? entries.join(' · ') : '—'
}

// Mirrors ContactSidebar.tsx's local `Section` helper exactly (same classes)
// — that helper isn't exported, so sibling self-contained sections (see also
// AddressesSection.tsx's `SectionHeader`) each carry their own copy.
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</h3>
      </div>
      {children}
    </div>
  )
}

export function MergeContactSection({ contact }: { contact: CrmContactDetail }) {
  const canDelete = usePermission('crm:delete')
  const { searchContacts, fetchMergePreview, mergeContacts } = useCRM()
  const { addToast } = useToast()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [candidates, setCandidates] = useState<CrmContactSummary[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [target, setTarget] = useState<CrmContactSummary | null>(null)
  const [preview, setPreview] = useState<MergePreview | null>(null)
  const [isMerging, setIsMerging] = useState(false)

  // Debounced candidate search (300ms — ContactListView convention).
  useEffect(() => {
    if (!open || target || !searchInput.trim()) {
      setCandidates([])
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    const handle = setTimeout(async () => {
      const results = await searchContacts(searchInput.trim())
      setCandidates(results.filter((c) => c.id !== contact.id))
      setIsSearching(false)
    }, 300)
    return () => clearTimeout(handle)
  }, [open, target, searchInput, contact.id, searchContacts])

  const handlePick = useCallback(async (candidate: CrmContactSummary) => {
    setTarget(candidate)
    const result = await fetchMergePreview(candidate.id, contact.id)
    if (!result) {
      addToast({
        type: 'error',
        title: 'Failed to load merge preview',
        description: 'Check your connection and try again.',
      })
      setTarget(null)
      return
    }
    setPreview(result)
  }, [contact.id, fetchMergePreview, addToast])

  const handleClose = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) {
      setSearchInput('')
      setCandidates([])
      setTarget(null)
      setPreview(null)
    }
  }, [])

  const handleMerge = useCallback(async () => {
    if (!target) return
    setIsMerging(true)
    const survivorId = await mergeContacts(target.id, contact.id)
    setIsMerging(false)
    if (!survivorId) {
      addToast({ type: 'error', title: 'Merge failed', description: 'The contacts were not merged.' })
      return
    }
    addToast({ type: 'success', title: 'Contacts merged' })
    handleClose(false)
    navigate(`/crm/contacts/${survivorId}`)
  }, [target, contact.id, mergeContacts, addToast, navigate, handleClose])

  if (!canDelete || contact.isDeleted) return null

  return (
    <>
      <Section title="Merge">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          leftIcon={<Merge className="w-4 h-4" />}
          className="w-full justify-start gap-2 font-normal text-muted-foreground"
        >
          Merge into…
        </Button>
      </Section>

      <Modal open={open} onOpenChange={handleClose}>
        <ModalContent size="lg">
          <ModalHeader>
            <ModalTitle>Merge {contact.displayName ?? 'contact'} into…</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {!target && (
              <div className="space-y-2">
                <Input
                  placeholder="Search contacts…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  autoFocus
                />
                {isSearching && <Spinner size="sm" />}
                {!isSearching && searchInput.trim() && candidates.length === 0 && (
                  <EmptyState title="No matching contacts" />
                )}
                {candidates.length > 0 && (
                  <ul className="space-y-1">
                    {candidates.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => handlePick(c)}
                          className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-muted transition-colors"
                        >
                          <span className="text-sm text-foreground">{c.displayName ?? c.channelUserId}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{c.channelUserId}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {target && !preview && (
              <div className="flex justify-center py-6">
                <Spinner size="sm" />
              </div>
            )}
            {target && preview && (
              <div className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Field</TableHead>
                      <TableHead>{contact.displayName ?? 'This contact'}</TableHead>
                      <TableHead>{target.displayName ?? 'Target'}</TableHead>
                      <TableHead>After merge</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.fields.map((f) => (
                      <TableRow key={f.field}>
                        <TableCell>{FIELD_LABELS[f.field] ?? f.field}</TableCell>
                        <TableCell>{formatValue(f.secondary)}</TableCell>
                        <TableCell>{formatValue(f.primary)}</TableCell>
                        <TableCell>{formatValue(f.result)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>AI insights</TableCell>
                      <TableCell>{insightCounts(preview.aiInsights.secondary)}</TableCell>
                      <TableCell>{insightCounts(preview.aiInsights.primary)}</TableCell>
                      <TableCell>{insightCounts(preview.aiInsights.result)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <p className="text-sm text-muted-foreground">
                  {[
                    `${preview.reassigned.conversations ?? 0} conversations`,
                    `${preview.reassigned.orders ?? 0} orders`,
                    `${preview.reassigned.deals ?? 0} deals`,
                    `${preview.reassigned.activities ?? 0} activities`,
                    `${preview.reassigned.addresses ?? 0} addresses`,
                  ].join(', ')}{' '}
                  will move to {target.displayName ?? 'the target contact'}.
                </p>
                <p className="text-sm text-muted-foreground">
                  {contact.displayName ?? 'This contact'} will be{' '}
                  {FATE_LABELS[preview.secondaryFate] ?? 'archived'} after the merge.
                </p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => handleClose(false)}>Cancel</Button>
            {target && (
              <Button variant="destructive" onClick={handleMerge} disabled={isMerging || !preview}>
                {isMerging ? <Spinner size="sm" className="mr-1" /> : null}
                Merge contacts
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
